/**
 * Cartera del Estudio Contable — dashboard operativo cross-empresa.
 *
 * KPIs de la cartera (summary) + una fila por empresa cliente con el semáforo
 * tributario (F29/F22), el estado de nómina del mes, DTEs por aceptar, documentos
 * vencidos (CxC/CxP) y el estado de cierre. El contador entra a cada empresa o
 * marca un F29/F22 como presentado sin cambiarse de empresa.
 */
import { useMemo, useState } from 'react'
import {
  Building2, Search, ExternalLink, CheckCircle2, Lock, LockOpen,
  Receipt, CalendarClock, FileWarning, RefreshCw, Users,
} from 'lucide-react'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { Badge } from '@/components/Badge'
import { EmptyState } from '@/components/EmptyState'
import { TableSkeleton } from '@/components/Skeleton'
import { KebabMenu } from '@/components/KebabMenu'
import { DataTable } from '@/components/ds/DataTable'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { ToastContainer, useToast } from '@/components/Toast'
import { getApiErrorMessage } from '@/lib/apiClient'
import {
  useFirmPortfolio, useMarkFiling, type PortfolioClient,
} from '@/modules/firm/hooks/useFirm'
import { SemaforoBadge, PayrollBadge, useOpenTenant, formatCLP, formatDate } from './firmShared'

// ─── Tarjeta KPI ────────────────────────────────────────────────────────────────

const KPI_COLORS = {
  blue: 'bg-primary-50/80 dark:bg-primary-900/30 text-primary-hover dark:text-primary-200',
  green: 'bg-green-50/80 dark:bg-green-900/30 text-green-700 dark:text-green-200',
  amber: 'bg-warning-surface text-warning-content',
  purple: 'bg-purple-50/80 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200',
  teal: 'bg-teal-50/80 dark:bg-teal-900/30 text-teal-700 dark:text-teal-200',
} as const

function Kpi({ title, value, icon: Icon, color }: {
  title: string; value: number; icon: React.ElementType; color: keyof typeof KPI_COLORS
}) {
  return (
    <div className={`${KPI_COLORS[color]} rounded-xl p-4 border border-divider/60 flex items-center justify-between`}>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide opacity-80">{title}</p>
        <p className="text-2xl font-bold mt-1 tabular-nums">{value}</p>
      </div>
      <Icon size={22} className="shrink-0 opacity-70" />
    </div>
  )
}

// ─── Celda de semáforo (con monto/vencimiento como tooltip) ───────────────────

function SemaforoCell({ s }: { s?: PortfolioClient['snapshot']['f29'] }) {
  const title = s
    ? `Período ${s.period} · ${formatCLP(s.monto)} · vence ${formatDate(s.due_date)}`
    : undefined
  return <span title={title}><SemaforoBadge s={s} /></span>
}

// ─── Página ───────────────────────────────────────────────────────────────────

export function FirmPortfolioPage() {
  const { data, isLoading, isError, refetch, isFetching } = useFirmPortfolio()
  const { openTenant, canOpen } = useOpenTenant()
  const markFiling = useMarkFiling()
  const toast = useToast()
  const { confirm, modalProps } = useConfirmModal()
  const [search, setSearch] = useState('')

  const clients = data?.by_client ?? []
  const summary = data?.summary

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return clients
    return clients.filter(
      (c) => c.tenant_name.toLowerCase().includes(q) || c.tenant_schema.toLowerCase().includes(q),
    )
  }, [clients, search])

  const handleMark = (c: PortfolioClient, kind: 'F29' | 'F22') => {
    const s = kind === 'F29' ? c.snapshot.f29 : c.snapshot.f22
    if (!s) return
    confirm({
      message: `¿Marcar el ${kind} de ${c.tenant_name} (período ${s.period}) como presentado?`,
      variant: 'success',
      confirmLabel: 'Marcar presentado',
      onConfirm: async () => {
        try {
          await markFiling.mutateAsync({ tenant_schema: c.tenant_schema, kind, period: s.period })
          toast.success(`${kind} de ${c.tenant_name} marcado como presentado.`)
        } catch (e) {
          toast.error(getApiErrorMessage(e))
        }
      },
    })
  }

  return (
    <div className="p-4 sm:p-6">
      <PageHeader
        title="Cartera del estudio"
        subtitle="Estado tributario y operativo de cada empresa cliente."
        help={HELP['firm.portfolio']}
      >
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-divider rounded-lg hover:bg-soft transition-colors disabled:opacity-60"
        >
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </PageHeader>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <Kpi title="Empresas" value={summary.clients} icon={Building2} color="blue" />
          <Kpi title="F29 pendientes" value={summary.f29_pending} icon={Receipt}
            color={summary.f29_pending > 0 ? 'amber' : 'green'} />
          <Kpi title="F22 pendientes" value={summary.f22_pending} icon={CalendarClock}
            color={summary.f22_pending > 0 ? 'amber' : 'green'} />
          <Kpi title="DTEs por aceptar" value={summary.dtes_por_aceptar} icon={FileWarning}
            color={summary.dtes_por_aceptar > 0 ? 'purple' : 'green'} />
          <Kpi title="Nóminas pendientes" value={summary.payroll_pending} icon={Users}
            color={summary.payroll_pending > 0 ? 'teal' : 'green'} />
        </div>
      )}

      {/* Buscador */}
      <div className="relative mb-3 max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
        <input aria-label="Buscar empresa"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar empresa…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-surface border border-divider text-content placeholder:text-content-subtle focus:outline-none focus:ring-2 focus:ring-focus-ring"
        />
      </div>

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass border border-divider rounded-card overflow-hidden shadow-card">
        {isError ? (
          <EmptyState icon={FileWarning} title="No se pudo cargar la cartera"
            hint="Intentar actualizar. Si el problema persiste, revisar el acceso al estudio." />
        ) : (
          <DataTable
            rows={filtered}
            rowKey={c => c.tenant_schema}
            loading={isLoading}
            loadingNode={<TableSkeleton rows={6} cols={8} />}
            minWidth={1180}
            caption="Cartera de empresas del contador"
            empty={
              <EmptyState icon={Building2} title="Sin empresas en la cartera"
                hint={search ? 'Ninguna empresa coincide con la búsqueda.' : 'Aún no hay empresas asignadas a esta cartera.'} />
            }
            columns={[
              {
                key: 'tenant_name', header: 'Empresa', sortable: true,
                render: c => canOpen(c.tenant_schema) ? (
                  <button onClick={() => openTenant(c.tenant_schema)} className="text-left group">
                    <span className="font-medium text-content group-hover:text-primary inline-flex items-center gap-1">
                      {c.tenant_name}
                      <ExternalLink size={12} className="opacity-0 group-hover:opacity-60" />
                    </span>
                    <span className="block text-xs text-content-subtle">{c.tenant_schema}</span>
                  </button>
                ) : (
                  <div>
                    <span className="font-medium text-content">{c.tenant_name}</span>
                    <span className="block text-xs text-content-subtle">{c.tenant_schema}</span>
                  </div>
                ),
              },
              {
                key: 'assigned_to', header: 'Contador', sortable: true,
                sortValue: c => c.assigned_to?.full_name ?? null,
                render: c => (
                  <span className="text-content-muted">
                    {c.assigned_to?.full_name || <span className="text-content-subtle">—</span>}
                  </span>
                ),
              },
              // ⚠️ Los tres semáforos se ordenan por su ESTADO y no por lo que
              // dibuja el badge: lo que interesa es agrupar lo pendiente.
              {
                key: 'f29', header: 'F29', sortable: true,
                sortValue: c => c.snapshot?.f29?.estado ?? null,
                render: c => <SemaforoCell s={c.snapshot?.f29} />,
              },
              {
                key: 'f22', header: 'F22', sortable: true,
                sortValue: c => c.snapshot?.f22?.estado ?? null,
                render: c => <SemaforoCell s={c.snapshot?.f22} />,
              },
              {
                key: 'payroll', header: 'Nómina', sortable: true,
                sortValue: c => c.snapshot?.payroll?.status ?? null,
                render: c => <PayrollBadge status={c.snapshot?.payroll?.status} />,
              },
              {
                key: 'dtes', header: 'DTEs', numeric: true, sortable: true, width: 90,
                sortValue: c => c.snapshot?.dtes_por_aceptar ?? 0,
                render: c => {
                  const dtes = c.snapshot?.dtes_por_aceptar ?? 0
                  return (
                    <div className="text-center">
                      {dtes > 0
                        ? <Badge label={String(dtes)} variant="purple" />
                        : <span className="text-content-subtle">0</span>}
                    </div>
                  )
                },
              },
              {
                key: 'vencidos', header: 'Vencidos', sortable: true, width: 150,
                // Se ordena por el TOTAL de documentos vencidos, que es lo que
                // decide a quién hay que atender primero.
                sortValue: c => (c.snapshot?.vencidos?.cxc ?? 0) + (c.snapshot?.vencidos?.cxp ?? 0),
                render: c => {
                  const cxc = c.snapshot?.vencidos?.cxc ?? 0
                  const cxp = c.snapshot?.vencidos?.cxp ?? 0
                  return cxc === 0 && cxp === 0 ? (
                    <span className="text-content-subtle">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {cxc > 0 && <Badge label={cxc + ' CxC'} variant="red" />}
                      {cxp > 0 && <Badge label={cxp + ' CxP'} variant="orange" />}
                    </div>
                  )
                },
              },
              {
                key: 'cierre', header: 'Cierre', sortable: true, width: 90,
                sortValue: c => {
                  const l = c.snapshot?.cierre?.current_date_locked
                  return l === undefined ? null : (l ? 1 : 0)
                },
                render: c => {
                  const locked = c.snapshot?.cierre?.current_date_locked
                  return (
                    <div className="text-center">
                      {locked === undefined ? (
                        <span className="text-content-subtle">—</span>
                      ) : locked ? (
                        <Lock size={15} className="inline text-success-solid" aria-label="Período bloqueado" />
                      ) : (
                        <LockOpen size={15} className="inline text-content-subtle" aria-label="Período abierto" />
                      )}
                    </div>
                  )
                },
              },
              {
                key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 60,
                render: c => (
                  <div className="text-right">
                    <KebabMenu items={[
                      {
                        label: 'Abrir empresa',
                        icon: <ExternalLink size={14} />,
                        onClick: () => openTenant(c.tenant_schema),
                        hidden: !canOpen(c.tenant_schema),
                      },
                      {
                        label: 'Marcar F29 presentado',
                        icon: <CheckCircle2 size={14} />,
                        onClick: () => handleMark(c, 'F29'),
                        hidden: !c.snapshot?.f29 || c.snapshot.f29.estado === 'presentado',
                      },
                      {
                        label: 'Marcar F22 presentado',
                        icon: <CheckCircle2 size={14} />,
                        onClick: () => handleMark(c, 'F22'),
                        hidden: !c.snapshot?.f22 || c.snapshot.f22.estado === 'presentado',
                      },
                    ]} />
                  </div>
                ),
              },
            ]}
          />
        )}
      </div>

      <ConfirmModal {...modalProps} />
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </div>
  )
}

export default FirmPortfolioPage
